import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  type ApolloClient,
  type NormalizedCacheObject,
  useApolloClient,
} from '@apollo/react-hooks';
import { string, date, object, array, number, boolean } from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import { AddCircleOutline, Clear } from '@mui/icons-material';
import { type FormikErrors, useFormik } from 'formik';
import dayjs from 'dayjs';
import { CreateTagDialog } from './CreateTagDialog';
import { PANEL_FORM_MODE } from './constants';
import {
  TextField,
  Select,
  IconButton,
  SwitchButton,
  Button,
  Alert,
  type AlertDetails,
  DatePicker,
  type HeadCell,
  Table,
  type SelectOption,
  Loading,
} from '../../../../../components';
import {
  PANEL_DETAIL_MEMBER_TEXT,
  PANEL_FORM_DIALOG_TEXT,
  PANEL_LIST_TEXT,
  PANEL_TAG_TEXT,
} from '../../../lang';
import {
  type PanelMember,
  type PanelFormData,
  useGetPanelMemberInfo,
  PANEL_STATUS,
} from '../../../graphql/GSAssessment';
import { handlePageLoading } from '../../../../../utils';
import {
  type FundingPanel,
  panelOptionWithMixed,
  panelOptionWithMixedAbbv,
} from '../../../../Nomination/components';

type PanelCreateFormDialogProps = {
  open: boolean;
  initialValues: PanelFormData;
  panelFormMode: PANEL_FORM_MODE;
  fundingsNeedPanel: SelectOption[];
  entryYears: SelectOption[];
  setRemove?: (remove: boolean) => void;
  submitPanel: (values: PanelFormData) => Promise<void>;
  onClose: () => void;
};

type PanelFormModeConfig = {
  showEditSwitch: boolean;
  formDisabled: boolean;
  buttonText?: string;
};
const panelFormModeConfigMap = (
  editModeOn: boolean,
): Record<PANEL_FORM_MODE, PanelFormModeConfig> => {
  const panelFormModeConfig: Record<PANEL_FORM_MODE, PanelFormModeConfig> = {
    [PANEL_FORM_MODE.CREATE]: {
      showEditSwitch: false,
      formDisabled: false,
      buttonText: PANEL_FORM_DIALOG_TEXT.CREATE_PANEL,
    },
    [PANEL_FORM_MODE.UPDATE]: {
      showEditSwitch: true,
      formDisabled: !editModeOn,
      buttonText: editModeOn ? PANEL_FORM_DIALOG_TEXT.SAVE : undefined,
    },
    [PANEL_FORM_MODE.VIEW]: {
      showEditSwitch: false,
      formDisabled: true,
      buttonText: PANEL_FORM_DIALOG_TEXT.CLOSE,
    },
  };
  return panelFormModeConfig;
};

export const PanelFormDialog: React.FC<PanelCreateFormDialogProps> = (
  props: PanelCreateFormDialogProps,
) => {
  const {
    open,
    panelFormMode,
    initialValues,
    fundingsNeedPanel,
    entryYears,
    submitPanel,
    onClose,
  } = props;
  const [editModeOn, setEditModeOn] = useState(false);
  const [pageloading, setPageLoading] = useState(false);
  const [openTagDialog, setOpenTagDialog] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const panelFormModeConfig = panelFormModeConfigMap(editModeOn)[panelFormMode];
  const { showEditSwitch, formDisabled, buttonText } = panelFormModeConfig;
  const { status } = initialValues;

  const enableEdit =
    status === PANEL_STATUS.PREPARED || status === PANEL_STATUS.IN_PROGRESS;

  const formik = useFormik<
    PanelFormData & {
      panelMemberSearch: string;
    }
  >({
    initialValues: { ...initialValues, panelMemberSearch: '' },
    validationSchema: object({
      title: string().required('Required'),
      fundingSource: string().required('Required'),
      entryYear: string().required('Required'),
      panelType: string().required('Required'),
      endDate: date().required('Required'),
      panelChair: string().required('Required'),
      panelMember: array().of(
        object({
          order: number().required('Required'),
          netId: string().required('Required'),
          name: string().required('Required'),
          department: string().required('Required'),
          email: string().required('Required'),
          classification: string().required('Required'),
          isPanelChair: boolean(),
        }),
      ),
      tagOptions: array().of(string()),
    }),
    onSubmit: async value => {
      // to check if add panel chair
      if (!handleCheckPanelChair()) {
        formik.setFieldError(
          'panelChair',
          'Please add panel chair to the list',
        );
        return;
      }

      await submitPanel(value);
    },
  });

  const handleCheckPanelChair = (): boolean => {
    return formik.values.panelMember.some(member => member.isPanelChair);
  };
  const handleOpenTagsDialog = (): void => {
    setOpenTagDialog(true);
  };

  const handleCloseTagsDialog = (): void => {
    setOpenTagDialog(false);
  };

  const handleSwitchEditMode = (): void => {
    // if close the edit mode
    if (editModeOn) {
      formik.resetForm();
    }
    setEditModeOn(!editModeOn);
  };

  const handleAddTag = async (tagName: string): Promise<void> => {
    await formik.setFieldValue('tagOptions', [
      ...formik.values.tagOptions,
      tagName,
    ]);
  };

  const handleRemoveTag = async (tagName: string): Promise<void> => {
    const updatedTags = formik.values.tagOptions.filter(
      option => option !== tagName,
    );
    await formik.setFieldValue('tagOptions', updatedTags);
  };

  const handleRemovePanelMember = async (netId: string): Promise<void> => {
    const hasPanelChairPlaceHolder = handleCheckPanelChair() ? 0 : 1;

    const updatedPanelMembers = formik.values.panelMember.filter(
      member => member.netId !== netId,
    );
    const reorderedPanelMembers = updatedPanelMembers.map((member, index) => ({
      ...member,
      order: index + hasPanelChairPlaceHolder,
    }));

    await formik.setFieldValue('panelMember', reorderedPanelMembers);
    setAlertDetails({
      alertContent: `Removed ${netId} Successfully.`,
      alertType: 'success',
    });
    setOpenAlert(true);
  };

  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const handleAddPanelMember = async (
    fieldName: 'panelChair' | 'panelMemberSearch',
  ): Promise<void> => {
    const curNetId = formik.values[fieldName];

    if (!curNetId) return;
    await handlePageLoading(setPageLoading, async () => {
      try {
        const res = (
          await useGetPanelMemberInfo(client, {
            netId: curNetId,
          })
        )?.[0];

        if (res) {
          if (fieldName === 'panelChair') {
            const updatePanelChair: PanelMember = {
              order: 0,
              netId: res?.netId ?? '',
              name: res?.displayName ?? '',
              department: res?.department ?? '',
              email: res?.email ?? '',
              classification: '',
              isPanelChair: true,
              givenName: res?.givenName ?? '',
              surname: res?.surname ?? '',
              title: res?.title ?? '',
              jobTitle: res?.jobTitle ?? '',
              institution: res?.institution ?? '',
            };
            // to replace current panel chair(if any)
            await formik.setFieldValue('panelMember', [
              updatePanelChair,
              ...formik.values.panelMember.filter(member => member.order !== 0),
            ]);
          }

          if (fieldName === 'panelMemberSearch') {
            const exists = formik.values.panelMember.some(
              member => member.netId === curNetId,
            );

            if (exists) {
              formik.setFieldError('panelMemberSearch', 'Already exists');
              return;
            }
            const hasPanelChair = handleCheckPanelChair();
            const updatePanelMember: PanelMember = {
              order: hasPanelChair
                ? formik.values.panelMember.length
                : formik.values.panelMember.length + 1,
              netId: res?.netId ?? '',
              name: res?.displayName ?? '',
              department: res?.department ?? '',
              email: res?.email ?? '',
              classification: '',
              isPanelChair: false,
              givenName: res?.givenName ?? '',
              surname: res?.surname ?? '',
              title: res?.title ?? '',
              jobTitle: res?.jobTitle ?? '',
              institution: res?.institution ?? '',
            };

            await formik.setFieldValue('panelMember', [
              ...formik.values.panelMember,
              updatePanelMember,
            ]);
            await formik.setFieldValue('panelMemberSearch', '');
          }
        } else {
          formik.setFieldError(fieldName, 'No match found');
        }
      } catch (error) {
        setAlertDetails({
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        });
        setOpenAlert(true);
      }
    });
  };

  const handleUpdateDiscipline = async (
    e: React.ChangeEvent<{ value: unknown }>,
    targetNetId: string,
  ): Promise<void> => {
    const discipline = e.target.value as FundingPanel;

    const updatedPanelMembers = formik.values.panelMember.map(member => {
      if (member.netId === targetNetId) {
        return {
          ...member,
          classification: discipline,
        };
      }
      return member;
    });

    await formik.setFieldValue('panelMember', updatedPanelMembers);
  };

  const panelListData: Array<HeadCell<PanelMember>> = [
    {
      id: 'order',
      label: PANEL_DETAIL_MEMBER_TEXT.NUMBER,
      isSortable: false,
      minWidth: 6,
      render: (row: PanelMember) => (row.order === 0 ? '0*' : row.order),
    },
    {
      id: 'netId',
      label: PANEL_DETAIL_MEMBER_TEXT.NETID,
      isSortable: false,
      minWidth: 10,
      render: (row: PanelMember) => row.netId,
    },
    {
      id: 'name',
      label: PANEL_DETAIL_MEMBER_TEXT.NAME,
      isSortable: false,
      render: (row: PanelMember) => row.name,
    },
    {
      id: 'department',
      label: PANEL_DETAIL_MEMBER_TEXT.DEP,
      isSortable: false,
      minWidth: 10,
      render: (row: PanelMember) => row.department,
    },
    {
      id: 'email',
      label: PANEL_DETAIL_MEMBER_TEXT.EMAIL,
      isSortable: false,
      render: (row: PanelMember) => row.email,
    },
    {
      id: 'classification',
      label: PANEL_DETAIL_MEMBER_TEXT.DISCIPLINES,
      isSortable: false,
      minWidth: 16,
      render: (row: PanelMember) => disciplineSelect(row),
    },
    {
      id: 'action',
      label: PANEL_DETAIL_MEMBER_TEXT.ACTION,
      isSortable: false,
      minWidth: 10,
      render: (row: PanelMember) => action(row),
    },
  ];

  const disciplineSelect = (row: PanelMember): React.ReactNode => {
    const { classification, netId } = row;
    const idx = formik.values.panelMember.findIndex(
      member => member.netId === netId,
    );

    const classificationError = (
      formik.errors.panelMember?.[idx] as FormikErrors<PanelMember>
    )?.classification;
    return (
      <Select
        options={panelOptionWithMixedAbbv}
        size="small"
        name={`panelMember${idx}.classification`}
        value={classification ?? ''}
        fullWidth
        disabled={formDisabled}
        onChange={async e => {
          await handleUpdateDiscipline(e, netId);
        }}
        error={
          formik.touched.panelMember?.[idx]?.classification &&
          Boolean(classificationError)
        }
        helperText={
          formik.touched.panelMember?.[idx]?.classification &&
          classificationError
        }
      />
    );
  };

  const action = (row: PanelMember): React.ReactNode => {
    const { netId, order } = row;
    return (
      <div className="flex justify-center items-center">
        <IconButton
          onClick={async () => {
            await handleRemovePanelMember(netId);
          }}
          color="primary"
          icon={<Clear />}
          // should not clear the panel chair
          disabled={formDisabled || order === 0}
        />
      </div>
    );
  };

  const panelMemberSearchInput = useMemo((): React.ReactNode => {
    return (
      <TextField
        size="small"
        disabled={formDisabled}
        name="panelMemberSearch"
        placeholder="Please Input NetID"
        value={formik.values.panelMemberSearch}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={
          formik.touched.panelMemberSearch &&
          Boolean(formik.errors.panelMemberSearch)
        }
        helperText={
          formik.touched.panelMemberSearch && formik.errors.panelMemberSearch
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                icon={<AddCircleOutline />}
                onClick={async () => {
                  await handleAddPanelMember('panelMemberSearch');
                }}
                disabled={formDisabled || !formik.values.panelMemberSearch}
                color="primary"
                sx={{ padding: '0 !important' }}
              />
            </InputAdornment>
          ),
        }}
      />
    );
  }, [
    formik.errors.panelMemberSearch,
    formik.touched.panelMemberSearch,
    formik.values.panelMemberSearch,
    formDisabled,
  ]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" scroll={'body'}>
      {pageloading && <Loading />}

      <div className="w-[56em]">
        <DialogTitle
          className="bg-[#951D1D] text-white text-base"
          fontWeight={'700'}
        >
          {PANEL_FORM_DIALOG_TEXT.PANEL_INFORMATION}
        </DialogTitle>
        <DialogContent className="py-1">
          <div className="flex flex-wrap items-center w-full gap-[0.5rem] px-2 py-4">
            <p className="font-semibold text-md">
              {PANEL_FORM_DIALOG_TEXT.PANEL_SETTING}
            </p>
            {showEditSwitch && (
              <>
                <p className="text-[#951D1D] text-sm font-semibold ml-[2rem] ml-[2rem]">
                  {PANEL_FORM_DIALOG_TEXT.EDIT}
                </p>
                <SwitchButton
                  value={editModeOn}
                  onChange={handleSwitchEditMode}
                  disabled={!enableEdit}
                />
              </>
            )}
          </div>
          <form noValidate onSubmit={formik.handleSubmit} className="!text-sm">
            <div className="bg-[#F6F6F6] p-4">
              <div className="grid grid-cols-[1fr,3fr,1fr,3fr] gap-4">
                <Lable>{PANEL_FORM_DIALOG_TEXT.ROUND_TITLE}</Lable>
                <TextField
                  name="title"
                  size="small"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={formDisabled}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                />

                <Lable>{PANEL_FORM_DIALOG_TEXT.DISCIPLINES}</Lable>
                <Select
                  options={panelOptionWithMixed}
                  name="panelType"
                  size="small"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={formDisabled}
                  value={formik.values.panelType}
                  error={
                    formik.touched.panelType && Boolean(formik.errors.panelType)
                  }
                  helperText={
                    formik.touched.panelType && formik.errors.panelType
                  }
                />
                <Lable>{PANEL_FORM_DIALOG_TEXT.ENTRY_YEAR}</Lable>
                <Select
                  className="col-span-3"
                  name="entryYear"
                  size="small"
                  options={entryYears}
                  value={formik.values.entryYear}
                  disabled={formDisabled}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.entryYear && Boolean(formik.errors.entryYear)
                  }
                  helperText={
                    formik.touched.entryYear && formik.errors.entryYear
                  }
                />

                <Lable>{PANEL_FORM_DIALOG_TEXT.FUNDING}</Lable>
                <Select
                  className="col-span-3"
                  name="fundingSource"
                  size="small"
                  options={fundingsNeedPanel}
                  value={formik.values.fundingSource}
                  disabled={formDisabled}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.fundingSource &&
                    Boolean(formik.errors.fundingSource)
                  }
                  helperText={
                    formik.touched.fundingSource && formik.errors.fundingSource
                  }
                />
              </div>
              <div className="grid grid-cols-[auto,auto,auto,auto] gap-4 mt-4">
                <Lable>{PANEL_LIST_TEXT.MEETING_DATE}</Lable>
                <DatePicker
                  disabled={formDisabled}
                  name="endDate"
                  size="small"
                  minDate={dayjs()}
                  required
                  InputLabelProps={{ shrink: true }}
                  value={
                    formik.values.endDate !== null
                      ? dayjs(formik.values.endDate)
                      : null
                  }
                  onChange={date => {
                    void formik.setFieldValue('endDate', date);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    Boolean(formik.touched.endDate) &&
                    Boolean(formik.errors.endDate)
                  }
                  helperText={
                    Boolean(formik.touched.endDate) && formik.errors.endDate
                  }
                />

                <Lable>{PANEL_FORM_DIALOG_TEXT.PANEL_CHAIR}</Lable>
                <TextField
                  name="panelChair"
                  size="small"
                  placeholder="Please Input NetID"
                  disabled={formDisabled}
                  value={formik.values.panelChair}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.panelChair &&
                    Boolean(formik.errors.panelChair)
                  }
                  helperText={
                    formik.touched.panelChair && formik.errors.panelChair
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          icon={<AddCircleOutline />}
                          onClick={async () => {
                            await handleAddPanelMember('panelChair');
                          }}
                          disabled={formDisabled || !formik.values.panelChair}
                          color="primary"
                          sx={{
                            padding: '0 !important',
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
              <div className="flex mt-4 items-start w-full pr-1">
                <p className="flex-none">{PANEL_FORM_DIALOG_TEXT.TAGS}</p>
                <div className="pl-16 ml-2 grow">
                  <div className="flex flex-wrap gap-2">
                    {formik.values.tagOptions.map((tag, index) => (
                      <Tooltip key={index} title={tag}>
                        <Chip
                          label={tag}
                          onDelete={async () => {
                            await handleRemoveTag(tag);
                          }}
                          color="primary"
                          disabled={formDisabled}
                          className="overflow-hidden text-ellipsis whitespace-nowrap"
                          style={{
                            maxWidth: '14em',
                          }}
                        />
                      </Tooltip>
                    ))}
                  </div>
                </div>
                <IconButton
                  className="flex-none"
                  disabled={formDisabled}
                  color="primary"
                  onClick={handleOpenTagsDialog}
                  sx={{ padding: '0 !important' }}
                  icon={<AddCircleOutline />}
                />
              </div>
            </div>

            {/* add panel member */}
            <div className="font-semibold p-2 text-md">
              <p>{PANEL_FORM_DIALOG_TEXT.PANEL_MEMBER}</p>
              <div className="flex justify-between">
                <Lable className="italic text-[#951D1D]">
                  {PANEL_FORM_DIALOG_TEXT.CHAIR_MARK}
                </Lable>
                <div className="flex flex-row gap-2">
                  <Lable>{PANEL_FORM_DIALOG_TEXT.ADD_MEMBER}</Lable>

                  {panelMemberSearchInput}
                </div>
              </div>
            </div>
            <div className="my-1 w-full over-flow-hidden">
              <Table<PanelMember>
                headCells={panelListData}
                data={formik.values.panelMember}
                height={'auto'}
                handleRequestSort={() => {}}
              />
            </div>
            <div className="flex justify-center mt-6 gap-[0.5rem]">
              {buttonText && (
                <Button
                  variant="contained"
                  type="submit"
                  className="!normal-case"
                >
                  {buttonText}
                </Button>
              )}
              <Button
                variant="outlined"
                className="!normal-case"
                onClick={onClose}
              >
                {formDisabled
                  ? PANEL_FORM_DIALOG_TEXT.CLOSE
                  : PANEL_FORM_DIALOG_TEXT.CANCEL}
              </Button>
            </div>
          </form>
        </DialogContent>
      </div>

      <CreateTagDialog
        open={openTagDialog}
        title={PANEL_TAG_TEXT.CREATE_PANEL_TAG}
        helpText={PANEL_TAG_TEXT.SET_TAGS}
        onClose={handleCloseTagsDialog}
        onCreate={handleAddTag}
      />
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
    </Dialog>
  );
};

const Lable = styled.div`
  margin-top: 0.55em;
`;
